<template>
  <overlay-component
    ref="overlay"
    :no-overlay="noOverlay"
    :width="250"
    @visible="show"
    @hidden="hide"
  >
    <div class="flex items-center leading-none">
      <div class="flex-shrink-0">
        <profile-picture
          v-if="!isLoading"
          :src="profilePictureUrl"
          :address="resolvedAddress"
          :size="picSize || 8"
          class="flex-grow-0 flex-shrink-0"
        />
        <div
          v-if="isLoading"
          :class="`w-${picSize || 8} h-${picSize || 8}`"
          class="bg-gray-800 rounded-full animate-pulse"
        />
      </div>
      <div
        v-if="onlyPic === false"
        class="flex content-center text-sm font-medium dark:text-white"
        :class="`ml-${Math.floor((picSize || 8) / 3)}`"
      >
        <component
          :is="itemComponentProps.is"
          v-bind="itemComponentProps"
          v-if="!isLoading"
        >
          {{ displayName.slice(0, 20) }}
          <i
            v-if="isVerified"
            class="text-blue-400 fas fa-badge-check"
            title="Verified on Opensea"
          />
        </component>
        <div
          v-if="isLoading"
          class="w-12 h-4 bg-gray-800 rounded animate-pulse"
        />
      </div>
    </div>
    <template
      v-if="!isLoading"
      #overlay-content
    >
      <div class="grid grid-cols-2">
        <metric-widget
          requires-premium
          :is-loading="isPendingLoad || isPortfolioValueLoading"
          :small="true"
          :value="portfolioValue"
          title="Portfolio value"
          :formatter="formatCurrency"
        />
        <metric-widget
          requires-premium
          :is-loading="isPendingLoad || isRealizedGainsLoading"
          :small="true"
          :value="realizedGains"
          title="Realized gains"
          :formatter="formatCurrency"
        />
      </div>
      <wallet-labels
        v-if="shouldLoadDetails"
        class="mt-2"
        :wallet="walletModel"
      />
      <div class="flex mt-3 space-x-2">
        <ui-button
          variant="white"
          size="xs"
          @click="$refs.editWalletName.show()"
        >
          <i class="far fa-edit" />
        </ui-button>
        <toggle-favorite
          v-if="hover"
          :id="walletModel.id"
          model="Wallet"
        >
          <template
            #default="{
              isFavorite,
              toggleFavorite,
              isToggling,
              isLoading: isLoadingFavoriteState,
            }"
          >
            <ui-button
              size="xs"
              class="col-span-2"
              :is-loading="isToggling || isLoadingFavoriteState"
              type="button"
              variant="white"
              @click="toggleFavorite"
            >
              <div>
                <i
                  class="fa-heart"
                  :class="isFavorite ? 'fas text-red-500' : 'far'"
                />
              </div>
              <span class="ml-2">{{
                isFavorite ? "Unfavorite" : "Favorite"
              }}</span>
            </ui-button>
          </template>
        </toggle-favorite>
        <ui-button
          v-if="walletModel"
          size="xs"
          tag="a"
          :href="`https://opensea.io/${walletModel.id}`"
          target="_blank"
          variant="white"
        >
          Opensea
        </ui-button>
      </div>
    </template>
    <wallet-display-name-modal
      ref="editWalletName"
      :wallet="walletModel"
    />
  </overlay-component>
</template>

<script>
import {
  computed,
  defineComponent,
  toRefs,
  ref,
} from "vue";
import { useQuery } from "@vue/apollo-composable";
import walletByAddressQuery from "@/graphql/wallet/queries/walletByAddress.query.gql";
import totalRealizedGainsQuery from "@/graphql/wallet/queries/totalRealizedGains.query.gql";
import totalPortfolioValueQuery from "@/graphql/wallet/queries/totalPortfolioValue.query.gql";


import ProfilePicture from "@/components/wallets/ProfilePicture";
import OverlayComponent from "../OverlayComponent.vue";
import MetricWidget from "../MetricWidget.vue";
import ToggleFavorite from "../ToggleFavorite.vue";
import UiButton from "../ui/UiButton.vue";
import WalletDisplayNameModal from "./WalletDisplayNameModal.vue";
import UiLink from "../ui/UiLink.vue";
import WalletLabels from './WalletLabels.vue';
import { formatCurrency } from "@/utils/filters";

export default defineComponent({
  name: "WalletProfile",

  components: {
    ProfilePicture,
    OverlayComponent,
    MetricWidget,
    ToggleFavorite,
    UiButton,
    WalletDisplayNameModal,
    WalletLabels,
  },

  props: {
    noOverlay: {
      type: Boolean,
      default: false,
    },
    wallet: null,
    address: null,
    picSize: null,
    onlyPic: {
      type: Boolean,
      default: false,
    },
    noLink: {
      type: Boolean,
      default: false,
    },
    isLoading: {
      type: Boolean,
      default: false,
    }
  },

  setup(props) {
    const {
      address,
      wallet,
      isLoading,
    } = toRefs(props);
    const shouldLoadDetails = ref(false);

    const {
      result: walletResult,
    } = useQuery(
      walletByAddressQuery,
      () => ({
        address: address.value,
      }),
      () => ({
        enabled: !isLoading.value && !wallet.value && address.value
      }),
    );

    const walletData = computed(() => {
      return walletResult.value?.wallet;
    });

    const walletModel = computed(() => {
      if (wallet?.value) {
        return wallet.value;
      }

      return walletData?.value && { id: address?.value };
    })

    const {
      result: totalRealizedGainsResult,
      loading: isRealizedGainsLoading,
    } = useQuery(
      totalRealizedGainsQuery,
      () => ({
        address: walletModel.value?.id,
      }),
      () => ({
        enabled: !isLoading.value && shouldLoadDetails.value && walletModel.value,
      })
    );

    const realizedGains = computed(() => {
      return totalRealizedGainsResult.value?.wallet?.analytics?.totalRealizedGains;
    });

    // queries above and below can be merged

    const {
      result: totalPortfolioValueResult,
      loading: isPortfolioValueLoading,
    } = useQuery(
      totalPortfolioValueQuery,
      () => ({
        address: walletModel.value?.id,
      }),
      () => ({
        enabled: !isLoading.value &&  walletModel.value?.id && shouldLoadDetails.value,
      })
    );

    const portfolioValue = computed(() => {
      return totalPortfolioValueResult.value?.wallet?.totalPortfolioValue;
    });

    return {
      walletData,
      walletModel,
      realizedGains,
      isRealizedGainsLoading,
      portfolioValue,
      isPortfolioValueLoading,
      formatCurrency,
      shouldLoadDetails,
    };
  },

  data() {
    return {
      hover: false,
      showTimeout: null,
      isPendingLoad: false,
      editWalletName: false,
      didLoad: false,
    };
  },
  computed: {
    isEditModalVisible() {
      return this.$refs.editWalletName?.isVisible ? "yes" : "no";
    },
    resolvedAddress() {
      return this.wallet?.id || this.address;
    },
    displayName() {
      return this.walletModel?.displayName || this.resolvedAddress?.slice(0, 6) || '';
    },
    profilePictureUrl() {
      return this.walletModel?.openseaProfile?.profilePictureUrl;
    },
    isVerified() {
      return this.walletModel?.openseaProfile?.isVerified;
    },
    itemComponentProps() {
      if (this.isLoading) {
        return {
          is: 'div'
        }
      }

      if (this.noLink === false) {
        return {
          is: UiLink,
          to: {
            name: "Wallet.Show",
            params: { address: this.resolvedAddress },
          },
        };
      }
      return {
        is: "div",
      };
    },
  },
  methods: {
    show() {
      if (this.showTimeout !== null) {
        clearTimeout(this.showTimeout);
      }
      this.showTimeout = setTimeout(() => {
        this.isPendingLoad = false;
        this.shouldLoadDetails = true;
        this.didLoad = true;
      }, 500);
      this.hover = true;
      if (!this.didLoad) {
        this.isPendingLoad = true;
      }
    },
    hide() {
      if (this.showTimeout !== null) {
        clearTimeout(this.showTimeout);
      }
      this.shouldLoadDetails = false;
      this.hover = false;
    },
  },
});
</script>

<style scoped>
</style>
